import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { ClipboardList, ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Personal Details" },
  { id: 2, title: "Employment & Income" },
  { id: 3, title: "Property & Mortgage" },
  { id: 4, title: "Financial Summary" }
];

export default function FactFinder() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [formData, setFormData] = useState({
    employment_status: "",
    annual_income: "",
    monthly_expenses: "",
    existing_debts: "",
    credit_score: "",
    property_type: "",
    first_time_buyer: false,
    deposit_amount: "",
    additional_info: ""
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/fact-finder`, {
        client_id: user.id,
        employment_status: formData.employment_status,
        annual_income: parseFloat(formData.annual_income) || 0,
        monthly_expenses: parseFloat(formData.monthly_expenses) || 0,
        existing_debts: parseFloat(formData.existing_debts) || 0,
        credit_score: parseInt(formData.credit_score) || null,
        property_type: formData.property_type,
        first_time_buyer: formData.first_time_buyer,
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        additional_info: formData.additional_info
      }, { headers: getAuthHeaders() });
      setComplete(true);
      toast.success("Fact finder submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit fact finder");
    }
    setLoading(false);
  };

  if (complete) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Fact Finder Complete!</h2>
            <p className="text-slate-400 mb-6">
              Thank you for providing your information. Our advisors will review your details shortly.
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="border-slate-700">
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate("/ai-analysis")} className="bg-blue-600 hover:bg-blue-700">
                Get AI Analysis
              </Button>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="fact-finder">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">Fact Finder</h1>
          <p className="text-slate-400">Help us understand your financial situation for the best mortgage advice.</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 max-w-2xl">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium ${
                currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : step.id}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 lg:w-24 h-0.5 mx-2 ${currentStep > step.id ? 'bg-blue-600' : 'bg-slate-800'}`}></div>
              )}
            </div>
          ))}
        </div>

        <Card className="bg-[#0f172a] border-slate-800 max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              {steps[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="first_time"
                    checked={formData.first_time_buyer}
                    onCheckedChange={(checked) => updateField("first_time_buyer", checked)}
                    data-testid="first-time-buyer-checkbox"
                  />
                  <Label htmlFor="first_time">I am a first-time buyer</Label>
                </div>
                <div className="space-y-2">
                  <Label>Estimated Credit Score (optional)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 750"
                    value={formData.credit_score}
                    onChange={(e) => updateField("credit_score", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    data-testid="credit-score-input"
                  />
                  <p className="text-xs text-slate-400">If you don't know your score, leave this blank</p>
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>Employment Status</Label>
                  <Select value={formData.employment_status} onValueChange={(v) => updateField("employment_status", v)}>
                    <SelectTrigger className="bg-[#020617] border-slate-700" data-testid="employment-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f172a] border-slate-700">
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="self_employed">Self-Employed</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Annual Income (£)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 50000"
                    value={formData.annual_income}
                    onChange={(e) => updateField("annual_income", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    data-testid="annual-income-input"
                  />
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => updateField("property_type", v)}>
                    <SelectTrigger className="bg-[#020617] border-slate-700" data-testid="property-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f172a] border-slate-700">
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="flat">Flat/Apartment</SelectItem>
                      <SelectItem value="bungalow">Bungalow</SelectItem>
                      <SelectItem value="new_build">New Build</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Deposit Amount (£)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 30000"
                    value={formData.deposit_amount}
                    onChange={(e) => updateField("deposit_amount", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    data-testid="deposit-amount-input"
                  />
                </div>
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>Monthly Expenses (£)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 1500"
                    value={formData.monthly_expenses}
                    onChange={(e) => updateField("monthly_expenses", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    data-testid="monthly-expenses-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Existing Debts (£)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 5000"
                    value={formData.existing_debts}
                    onChange={(e) => updateField("existing_debts", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    data-testid="existing-debts-input"
                  />
                  <p className="text-xs text-slate-400">Include credit cards, loans, etc.</p>
                </div>
                <div className="space-y-2">
                  <Label>Additional Information (optional)</Label>
                  <Textarea
                    placeholder="Any other information that might be relevant..."
                    value={formData.additional_info}
                    onChange={(e) => updateField("additional_info", e.target.value)}
                    className="bg-[#020617] border-slate-700"
                    rows={4}
                    data-testid="additional-info-textarea"
                  />
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={currentStep === 1}
                className="border-slate-700"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              {currentStep < 4 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="next-step-btn"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-fact-finder-btn"
                >
                  {loading ? "Submitting..." : "Submit"} <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
